import { useEffect, useRef, useState } from "react";
import { MessageSquare, Send, Sparkles } from "lucide-react";
import { C } from "../../lib/api";

// Table chat (bottom-left): 25-character messages + quick phrases; the server masks profanity as "###".
export const TableChat = ({ messages, send, canChat, myUserId, tableId }) => {
  const draftKey = `pfp_chat_draft_${tableId}`;
  const [text, setTextState] = useState(() => sessionStorage.getItem(draftKey) || ""); // half-typed lines survive re-renders/reloads
  const setText = (v) => { setTextState(v); if (v) sessionStorage.setItem(draftKey, v); else sessionStorage.removeItem(draftKey); };
  const [presets, setPresets] = useState(false);
  const [open, setOpen] = useState(false); // history drawer (lines pop up as bubbles at the avatars)
  const listRef = useRef(null);
  useEffect(() => { listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" }); }, [messages.length]);
  const submit = (t) => {
    const v = (t ?? text).trim().slice(0, C.CHAT.MAX_CHARS);
    if (!v || !canChat) return;
    send(C.WsClientEvent.CHAT, { text: v });
    setText(""); setPresets(false);
  };
  return (
    <div data-testid="table-chat" className="absolute left-4 bottom-4 z-30 w-[300px] flex flex-col gap-1.5">
      {presets && (
        <div data-testid="chat-presets" className="chat-box rounded-xl p-2 grid grid-cols-2 gap-1 card-slide">
          {C.CHAT.PRESETS.map((p, i) => (
            <button key={i} data-testid={`chat-preset-${i}`} onClick={() => submit(p)} className="rounded-md bg-white/5 hover:bg-amber-500/30 text-left text-[11px] px-2 py-1.5 text-amber-50 truncate">{p}</button>
          ))}
        </div>
      )}
      {open && (
        <div ref={listRef} data-testid="chat-messages" className="chat-box rounded-xl px-3 py-2 h-[108px] overflow-y-auto pane-scroll text-[12px] leading-snug flex flex-col gap-0.5 scroll-thin card-slide">
          {messages.length === 0 && <div className="text-slate-500 text-[11px] my-auto text-center">No messages yet — 25 characters max.</div>}
          {messages.map((m) => (
            <div key={m.id} data-testid="chat-message" className="break-words">
              <span className={`font-medium ${m.user_id === myUserId ? "text-amber-300" : "text-cyan-300"}`}>{m.name}:</span> <span className="text-slate-100">{m.text}</span>
            </div>
          ))}
        </div>
      )}
      <form onSubmit={(e) => { e.preventDefault(); submit(); }} className="chat-box rounded-full flex items-center gap-1 pl-1 pr-1 py-1">
        <button type="button" data-testid="chat-toggle" onClick={() => setOpen((o) => !o)} title={open ? "Hide chat history" : "Chat history"} className={`p-1.5 rounded-full hover:bg-white/5 ${open ? "text-amber-300" : "text-amber-200/80 hover:text-amber-200"}`}><MessageSquare className="w-4 h-4" /></button>
        <input data-testid="chat-input" value={text} onChange={(e) => setText(e.target.value.slice(0, C.CHAT.MAX_CHARS))} maxLength={C.CHAT.MAX_CHARS} disabled={!canChat}
          placeholder={canChat ? "Message…" : "Take a seat to chat"} className="flex-1 min-w-0 bg-transparent text-sm text-amber-50 placeholder:text-slate-500 outline-none px-1 disabled:opacity-60" />
        <span data-testid="chat-count" className={`font-mono text-[10px] ${text.length >= C.CHAT.MAX_CHARS ? "text-red-300" : "text-slate-500"}`}>{C.CHAT.MAX_CHARS - text.length}</span>
        <button type="button" data-testid="chat-presets-toggle" onClick={() => setPresets((p) => !p)} disabled={!canChat} title="Quick phrases" className="p-1.5 rounded-full text-amber-300 hover:bg-white/5 disabled:opacity-40"><Sparkles className="w-4 h-4" /></button>
        <button type="submit" data-testid="chat-send" disabled={!canChat || !text.trim()} className="p-1.5 rounded-full bg-amber-500 text-slate-900 disabled:opacity-40 btn-lift"><Send className="w-3.5 h-3.5" /></button>
      </form>
    </div>
  );
};
